import { Label } from '~/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { useCanChooseRetailerPrompt } from '~/hooks/useCanChooseRetailerPrompt';
import { useRetailerConfigs } from '~/hooks/useRetailerConfigs';
import { cn } from '~/utils/cn';

/** Radix Select forbids an empty-string item value, so "no retailer" needs a sentinel. */
const NO_RETAILER = '__shared__';

interface RetailerPromptSelectProps {
  /** The selected slug; `''` means no retailer, i.e. the shared prompt. */
  value: string;
  onChange: (retailer: string) => void;
  /**
   * The vertical being rendered, when there is exactly one (the prompt preview). Omitted on
   * enrichment runs, where each product's vertical is detected, so the hint lists coverage.
   */
  bucket?: string;
  id?: string;
  /**
   * `inline` (default) is a compact label · select · hint row for toolbars and cards. `stacked`
   * matches the enrichment panel's sections: heading, hint as description, full-width select.
   */
  layout?: 'inline' | 'stacked';
  /** Automation hook (default `retailer-prompt-select`); derives `-trigger`, `-content`, `-option-{slug}`, `-hint`. */
  testId?: string;
}

/**
 * Picks whose prompt rules google-agent applies — orthogonal to the vertical, which the agent
 * still detects. Renders nothing for non-`@lily.ai` users, who always get the shared prompt.
 */
export function RetailerPromptSelect({
  value,
  onChange,
  bucket,
  id = 'retailer-prompt',
  layout = 'inline',
  testId = 'retailer-prompt-select',
}: RetailerPromptSelectProps) {
  const canChoose = useCanChooseRetailerPrompt();
  const retailers = useRetailerConfigs(canChoose);
  if (!canChoose) return null;

  const list = retailers.data ?? [];
  const selected = list.find((r) => r.retailer === value);
  const unavailable = retailers.isError || (retailers.isSuccess && list.length === 0);

  let hint = 'Shared prompt for every vertical.';
  if (retailers.isError) hint = 'Could not load retailer prompts — the shared prompt is used.';
  else if (retailers.isSuccess && list.length === 0)
    hint = 'No retailer prompts ship in this deployment.';
  else if (selected && !bucket)
    hint = `${selected.retailer} has prompts for ${selected.buckets.join(', ')}. Products in other verticals use the shared prompt.`;
  else if (selected && bucket && selected.buckets.includes(bucket))
    hint = `Renders ${selected.retailer}'s own ${bucket} prompt.`;
  else if (selected && bucket)
    hint = `${selected.retailer} ships no ${bucket} prompt, so the shared one renders.`;

  const select = (
    <Select
      // A preselected retailer this list doesn't offer (still loading, or not shipped)
      // reads as the shared prompt rather than a blank box.
      value={value === '' || !selected ? NO_RETAILER : value}
      onValueChange={(v) => {
        onChange(v === NO_RETAILER ? '' : v);
      }}
      disabled={unavailable || retailers.isPending}
    >
      <SelectTrigger
        id={id}
        data-testid={`${testId}-trigger`}
        className={cn(
          layout === 'inline' && 'h-8 w-64 text-xs',
          unavailable ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
      >
        <SelectValue placeholder={retailers.isPending ? 'Loading…' : 'None (shared prompt)'} />
      </SelectTrigger>
      <SelectContent data-testid={`${testId}-content`}>
        <SelectItem
          value={NO_RETAILER}
          className="cursor-pointer"
          data-testid={`${testId}-option-shared`}
        >
          None (shared prompt)
        </SelectItem>
        {list.map((r) => (
          <SelectItem
            key={r.retailer}
            value={r.retailer}
            className="cursor-pointer"
            data-testid={`${testId}-option-${r.retailer}`}
          >
            {r.retailer}
            <span className="text-muted-foreground ml-1">— {r.buckets.join(', ')}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  // Stacked: heading + description above a full-width control, like the panel's sibling sections.
  if (layout === 'stacked') {
    return (
      <div className="space-y-3" data-testid={testId}>
        <div>
          <Label htmlFor={id} className="text-foreground block text-sm font-semibold">
            Retailer prompt
          </Label>
          <p className="text-muted-foreground text-xs" data-testid={`${testId}-hint`}>
            {hint}
          </p>
        </div>
        {select}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-3" data-testid={testId}>
      <Label htmlFor={id} className="text-xs">
        Retailer prompt
      </Label>
      {select}
      <p className="text-muted-foreground text-xs" data-testid={`${testId}-hint`}>
        {hint}
      </p>
    </div>
  );
}
